/**
 * Shared visual tokens for the app (colors, shadows, fonts, motion).
 * Prefer importing from here instead of scattering raw hex/rgb in components and pages.
 */

// ============================================================================
// Color tokens — solid fills
// ============================================================================

/** Pure white: surfaces, inverse text, icons on dark backgrounds. */
export const colorWhite = '#ffffff'

/** Backward-compatible alias (legacy imports). */
export const whitePalette = colorWhite

/** Pure black: strong text, hover emphasis on light links. */
export const colorBlack = '#000000'

/**
 * H2-level section titles on light marketing backgrounds (landing, cards).
 * Centralized foreground for block headings — same value as {@link colorBlack}.
 */
export const colorSectionHeadingOnLight = colorBlack

/** Dark gray — compact header search input text (light-on-dark variant uses white alphas). */
export const colorGrayNeutralStrong = '#333333'

/** Deep charcoal — help / legal secondary lines. */
export const colorGrayNeutralDeep = '#323232'

/** Medium gray — descriptive copy on light surfaces. */
export const colorGrayNeutralMid = '#707070'

/** Muted gray — tertiary actions, “dismiss” links. */
export const colorGrayNeutralMuted = '#6F6F6F'

/** Cool light gray — page shell (e.g. item details). */
export const colorSurfaceCoolGray = '#f3f4f6'

/** Neutral light gray — image slider / rail backgrounds. */
export const colorSurfaceLightGray = '#f4f4f6'

/** Indigo 500 — legacy accent (icons, links). */
export const colorAccentIndigo = '#3f51b5'

/** Brand teal — nonprofit CTAs, key outlines. */
export const colorAccentTeal = '#12adb8'

/** Skeleton / shimmer — light band. */
export const colorSkeletonStripeLight = '#E8E8E8'

/** Skeleton / shimmer — mid band. */
export const colorSkeletonStripeMid = '#dddddd'

/**
 * Horizontal loading shimmer (image placeholders, cards, sliders).
 * Keep in sync wherever skeleton UI appears.
 */
export const skeletonShimmerGradient = `linear-gradient(to right, ${colorSkeletonStripeLight} 10%, ${colorSkeletonStripeMid} 40%, ${colorSkeletonStripeLight} 70%)`

/** Header “Donate” control — resting background (custom green, not MUI primary). */
export const colorHeaderDonateBackground = '#3fb5a3'

/** Header “Donate” control — hover background. */
export const colorHeaderDonateBackgroundHover = '#52d5c1'

/** Staging / non-production environment banner. */
export const colorStagingBannerBackground = '#f96406'

/** “Not active” badge / icon fill (amber). */
export const colorStatusNotActiveAmber = '#ffb74d'

/** Create React App default dev shell background (see `app.css`). */
export const colorDevShellBackground = '#282c34'

/** Create React App default link color on dev shell. */
export const colorDevShellLink = '#61dafb'

// ============================================================================
// Color tokens — black with alpha
// ============================================================================

/** Subtle tint — search bar on primary (dark) header. */
export const colorBlackAlpha05 = 'rgba(0, 0, 0, 0.05)'

/** Light overlay — hover chrome, step cards, icon buttons. */
export const colorBlackAlpha10 = 'rgba(0, 0, 0, 0.1)'

/** Drawer / modal scrim (used with blur in `drawerBackdropBlurStyle`). */
export const colorBlackAlpha35 = 'rgba(0, 0, 0, 0.35)'

/** Muted body / meta text on light backgrounds. */
export const colorBlackAlpha40 = 'rgba(0, 0, 0, 0.4)'

/** Secondary text, placeholders, dividers. */
export const colorBlackAlpha50 = 'rgba(0, 0, 0, 0.5)'

/** Dark control wash — e.g. delete overlay on thumbnails. */
export const colorBlackAlpha60 = 'rgba(0, 0, 0, 0.6)'

/** Icon / control emphasis on light (search button hover text). */
export const colorBlackAlpha70 = 'rgba(0, 0, 0, 0.7)'

/** High-emphasis UI text (near Material default body1 contrast). */
export const colorBlackAlpha87 = 'rgba(0, 0, 0, 0.87)'

// ============================================================================
// Color tokens — white with alpha
// ============================================================================

/** Marketing / FAQ hairline and glass panels on gradient. */
export const colorWhiteAlpha05 = 'rgba(255, 255, 255, 0.05)'

/** Step cards on primary gradient — resting glass fill. */
export const colorWhiteAlpha08 = 'rgba(255, 255, 255, 0.08)'

/** Footer link hover / FAQ accordion hover wash. */
export const colorWhiteAlpha10 = 'rgba(255, 255, 255, 0.1)'

/** FAQ accordion pressed / focus ring. */
export const colorWhiteAlpha12 = 'rgba(255, 255, 255, 0.12)'

/** Step card hover fill on gradient. */
export const colorWhiteAlpha15 = 'rgba(255, 255, 255, 0.15)'

/** Stronger glass highlight on gradient sections. */
export const colorWhiteAlpha20 = 'rgba(255, 255, 255, 0.2)'

/** Translucent borders on primary gradient (FAQ, steps). */
export const colorWhiteAlpha30 = 'rgba(255, 255, 255, 0.3)'

/** Frosted listing toolbars (draft / purchased / my listings / moderation). */
export const colorWhiteAlpha40 = 'rgba(255, 255, 255, 0.4)'

/** Upload area progress veil. */
export const colorWhiteAlpha70 = 'rgba(255, 255, 255, 0.7)'

/** Profile / glass cards on photos. */
export const colorWhiteAlpha85 = 'rgba(255, 255, 255, 0.85)'

/** Compact (default) header search field background. */
export const colorWhiteAlpha90 = 'rgba(255, 255, 255, 0.9)'

/** Compact header search — focus state. */
export const colorWhiteAlpha95 = 'rgba(255, 255, 255, 0.95)'

/** Primary-on-gradient body text (steps, FAQ answers). */
export const colorWhiteAlpha95Text = 'rgba(255, 255, 255, 0.95)'

/** Footer legal row on primary dark band. */
export const colorWhiteAlpha80 = 'rgba(255, 255, 255, 0.8)'

/** Full-app canvas behind routed content (light neutral). */
export const colorAppCanvasBackground = 'rgba(245, 245, 245, 1)'

// ============================================================================
// Gradients (multi-stop)
// ============================================================================

/** Top tint for marketing welcome strip (fades to white). */
export const colorWelcomeGradientTop = 'rgba(235, 242, 255, 1)'

/** Main landing welcome section background. */
export const gradientMainWelcomeSection = `linear-gradient(180deg, ${colorWelcomeGradientTop} 0%, ${colorWhite} 100%)`

/**
 * Fade mask when long description is collapsed (white text on white card).
 * Used on item / moderator / listed detail expandable blocks.
 */
export const gradientExpandableTextCollapseMask =
  'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 75%, rgba(255,255,255,0.7) 85%, rgba(255,255,255,1) 100%)'

/** Shimmer over logo / loading chip in header (transparent → frosted white). */
export const gradientHeaderLogoLoading = 'linear-gradient(to right, #ffffff00 10%, #ffffff21 40%, #ffffff00 70%)'

// ============================================================================
// Shadows & text shadows
// ============================================================================

/** Shadow for “paper” surfaces: forms, cards, search filter, page wrappers */
export const elevatedSurfaceBoxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)'

/** Circular nav buttons on image gallery (light pill). */
export const shadowImageGalleryNavPill = '0 2px 10px rgba(0, 0, 0, 0.12)'

/** Join Hands section — elevated cards at rest. */
export const shadowMainJoinHandsCard = '0 20px 60px rgba(0, 0, 0, 0.3)'

/** Join Hands section — card hover lift. */
export const shadowMainJoinHandsCardHover = '0 28px 80px rgba(0, 0, 0, 0.4)'

/** Steps section — card hover on gradient. */
export const shadowMainStepsCardHover = '0 12px 32px rgba(0, 0, 0, 0.3)'

/** Notification list rows / drawer entries. */
export const shadowNotificationListItem = '0 4px 26px rgba(0, 0, 0, 0.1)'

/** Logo / wordmark on dark hero imagery (both header variants). */
export const textShadowLogoOnDarkHero = '0 0 20px rgb(0 0 0 / 90%)'

// ============================================================================
// Typography
// ============================================================================

/** Primary UI font stack (matches MUI theme overrides). */
export const appFontFamily =
  '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif'

// ============================================================================
// Layout
// ============================================================================

/** Primary content max width (px) — page shell rail and `MuiContainer` `maxWidth="lg"`. */
export const layoutContentMaxWidthPx = 1640

// ============================================================================
// Overlays & motion
// ============================================================================

/** MUI Modal `BackdropProps` / drawer overlay: dim + frosted blur */
export const drawerBackdropBlurStyle = {
  backgroundColor: colorBlackAlpha35,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
}

/** Single app-wide transition (hover, color, shadow, transform) */
export const transition = 'all 0.2s ease'

/**
 * Absolutely positioned layer flush with the edges of its containing block.
 * Use inside `position: relative` parents (image loaders, scrims, tap overlays).
 */
export const fullsizeAbsolute = {
  position: 'absolute' as const,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
}

/**
 * Product card status pills: `MuiBadge` on the image and the inline “NEW / USED” chip
 * next to “Condition:”. Used by `theme.overrides.MuiBadge` and `listed-item` styles.
 *
 * To change sizing/typography everywhere: edit this object.
 * To override only `Badge` via MUI: set `overrides.MuiBadge.badge` in `theme/index.ts`
 * (higher-specificity rules in components, e.g. order page `badgeHolder`, still win).
 */
export const productCardBadge = {
  fontSize: '0.625rem',
  lineHeight: 1.2,
  letterSpacing: '0.35px',
  textTransform: 'uppercase' as const,
  /** `MuiBadge` badge span — px padding matches ~`theme.spacing(0.375, 1)` when spacing factor is 8 */
  muiBadgePadding: '3px 8px',
  fontWeightBadge: 700,
  /** Inline chip next to “Condition:” */
  fontWeightInline: 600,
  inlinePaddingY: 0.125,
  inlinePaddingX: 0.5,
} as const

/**
 * Metrics for the primary (first) button in `useActionButtonRowStyles` rows; icon-only
 * siblings use compact fixed size in that hook.
 */
export const actionButtonRow = {
  minHeightPx: 48,
  padding: '12px 16px',
  fontSize: '0.9375rem',
  fontWeight: 600,
  borderRadius: 8,
} as const
